import { Spinner, Box, HStack, Text, Input, Pressable, useMediaQuery } from "native-base";
import { useEffect, useState } from "react";
import NoDataFound from "../../../../components/noDataFound";
import { getQuotationRequestsAPI, approveQuotationRequestAPI } from "../../../../api/apis/quicklinks";
import { Toaster } from "../../../../components/toaster";
import { COLORS } from "../../../../utils/colors";
import { COMMONFONTSIZE } from "../../../../utils/fonts";
import { MOBILE_VIEW_WIDTH } from "../../../../utils/screens";
import CustomButton from "../../../../components/button";
import CustomModal from "../../../../components/modal";
import MobileModelSheet from "../../../../components/actionSheet";
import { LoaderAnimation } from "../../../../components/spinner";
import Svg from 'react-native-svg';

const QuotationRequests = () => {
  const toaster = Toaster();
  const [MobileWindow] = useMediaQuery({ maxWidth: MOBILE_VIEW_WIDTH });

  // Quotation requests listing states
  const [requests, setRequests] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingRequests, setIsLoadingRequests] = useState(false);
  const [paginationInfo, setPaginationInfo] = useState({ page: 1, total: 0 });

  // Confirmation modal states
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [actionType, setActionType] = useState(''); // 'approve' or 'reject'
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    setIsLoading(true);
    getRequestsList(1, false);
  }, []);

  useEffect(() => {
    // Set loading to false after requests data is fetched (whether empty or not)
    if (!isLoadingRequests) {
      setIsLoading(false);
    }
  }, [isLoadingRequests]);

  // Quotation requests listing functions
  const getRequestsListWithParams = async (page = 1, append = false, search = null, isSearch = false) => {
    setIsLoadingRequests(true);
    // Show LoaderAnimation only for search operations (not initial load or load more)
    if (isSearch && !append) {
      LoaderAnimation.show();
    }
    try {
      // Use provided parameters or fall back to state values
      const searchParam = search !== null ? search : searchQuery.trim();

      const response = await getQuotationRequestsAPI(page, 10, searchParam);
      if (response.error) {
        toaster(response.message || 'Failed to fetch quotation requests', 'error', 'error');
        setIsLoadingRequests(false);
        setRequests([]);
        if (isSearch && !append) {
          LoaderAnimation.hide();
        }
        return;
      }

      // Handle the actual response structure
      // Console shows structure: response.data.data.data = {data: Array(1), page: 1, total: 1}
      // So we need: response.data.data.data.data for the array
      const requestsData = response.data?.data?.data?.data || response.data?.data?.data || [];
      const responsePage = response.data?.data?.data?.page || response.data?.data?.page || 1;
      const total = response.data?.data?.data?.total || response.data?.data?.total || 0;

      // Calculate items per page (default 10)
      const perPage = 10;
      const totalPages = Math.ceil(total / perPage);

      if (append) {
        setRequests(prev => [...prev, ...requestsData]);
      } else {
        setRequests(requestsData);
      }

      // Update pagination state
      setPaginationInfo({ page: responsePage, total });
      setHasMore(responsePage < totalPages && requestsData.length > 0);

    } catch (error) {
      console.error('Error fetching quotation requests:', error);
      toaster('Error fetching quotation requests', 'error', 'error');
    } finally {
      setIsLoadingRequests(false);
      if (isSearch && !append) {
        LoaderAnimation.hide();
      }
    }
  };

  const getRequestsList = async (page = 1, append = false) => {
    await getRequestsListWithParams(page, append, null);
  };

  const handleSearch = () => {
    setCurrentPage(1);
    getRequestsListWithParams(1, false, searchQuery.trim(), true);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    setCurrentPage(1);
    getRequestsListWithParams(1, false, '', true);
  };

  const handleLoadMore = () => {
    const nextPage = currentPage + 1;
    setCurrentPage(nextPage);
    getRequestsList(nextPage, true);
  };

  // Approve/Reject functionality
  const handleApproveClick = (request) => {
    setSelectedRequest(request);
    setActionType('approve');
    setShowConfirmModal(true);
  };

  const handleRejectClick = (request) => {
    setSelectedRequest(request);
    setActionType('reject');
    setShowConfirmModal(true);
  };

  // Scroll to top function
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const handleConfirmAction = async () => {
    if (!selectedRequest || !actionType) return;

    setIsProcessing(true);
    LoaderAnimation.show();

    try {
      const request_id = selectedRequest?._id;
      const reason = actionType; // "approve" or "reject"

      const response = await approveQuotationRequestAPI(request_id, reason);

      if (response.error) {
        console.error('Approve/Reject API Error:', response);
        toaster(response.message || `Failed to ${actionType} quotation request`, 'error', 'error');
        return;
      }

      toaster(response?.data?.data?.message || `Quotation request ${actionType}d successfully`, 'success', 'success');

      setShowConfirmModal(false);
      setSelectedRequest(null);
      setActionType('');

      // Scroll to top
      scrollToTop();

      // Refresh the requests list
      getRequestsList(1, false);
    } catch (error) {
      console.error(`Error ${actionType}ing quotation request:`, error);
      toaster(`Error ${actionType}ing quotation request`, 'error', 'error');
    } finally {
      setIsProcessing(false);
      LoaderAnimation.hide();
    }
  };

  const closeConfirmModal = () => {
    setShowConfirmModal(false);
    setSelectedRequest(null);
    setActionType('');
  };

  // Clear icon SVG
  const clearIconSVG = (
    <path
      d='M18 6L6 18M6 6L18 18'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  );

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;

      const formattedDate = new Intl.DateTimeFormat("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      }).format(date);

      return formattedDate;
    } catch (error) {
      return dateString;
    }
  };

  // Get status text color
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'pending':
        return COLORS.THEME_COLOR;
      case 'approved':
        return COLORS.SUCCESS_TOAST_COLOR;
      case 'rejected':
        return COLORS.ERROR_MESSAGE_COLOR;
      default:
        return COLORS.BLACK_COLOR;
    }
  };

  // Confirmation modal body
  const getConfirmModalBody = () => {
    const actionText = actionType === 'approve' ? 're-consider' : actionType;
    return (
      <Box justifyContent="center" alignItems="center">
        <Text
          fontSize={COMMONFONTSIZE.EXTRA_LARGE_FONT_SIZE}
          fontWeight="medium"
          textAlign="center"
        >
          Are you sure you want to {actionText} the quotation request for{' '}
          <Text fontWeight="bold">
            {selectedRequest?.vendor_name || 'N/A'}
          </Text>?
        </Text>
      </Box>
    );
  };

  // Confirmation modal footer
  const getConfirmModalFooter = () => {
    return (
      <HStack justifyContent="flex-end" width="100%" space={4}>
        <CustomButton
          text="Cancel"
          onPress={closeConfirmModal}
          isOutline
          bgColor={COLORS.BLACK_COLOR}
          textColor={COLORS.BLACK_COLOR}
        />
        <CustomButton
          text={isProcessing ? `${actionType === 'approve' ? 'Re-considering' : 'Rejecting'}...` : `${actionType === 'approve' ? 'Proceed with Re-consideration' : 'Confirm Reject'}`}
          onPress={handleConfirmAction}
          isLoading={isProcessing}
          isOutline
          bgColor={actionType === 'approve' ? COLORS.SUCCESS_TOAST_COLOR : COLORS.REJECT_TEXT_COLOR}
          textColor={actionType === 'approve' ? COLORS.SUCCESS_TOAST_COLOR : COLORS.REJECT_TEXT_COLOR}
        />
      </HStack>
    );
  };

  return (
    <>
      {isLoading ? (
        <Box flex={1} alignItems="center" justifyContent="center">
          <Spinner color={COLORS.THEME_COLOR} size="lg" />
        </Box>
      ) : (
        <Box>
          {/* Search Section */}
          {(requests?.length > 0 || searchQuery.trim()) && (
            <HStack justifyContent={'end'} alignItems={'center'} mb={4}>
              <HStack space={4} alignItems={'center'} flexWrap={MobileWindow ? 'wrap' : 'nowrap'}>
                {/* Search Input with Clear Icon */}
                <Box position='relative'>
                  <Input
                    placeholder='Search quotation requests...'
                    value={searchQuery}
                    onChangeText={(text) => {
                      // Only remove leading spaces, preserve trailing and middle spaces
                      const trimmedText = text?.trimStart();
                      setSearchQuery(trimmedText);
                      // If user clears the input with backspace, trigger search immediately
                      if (!trimmedText) {
                        setCurrentPage(1);
                        getRequestsListWithParams(1, false, '', true);
                      }
                    }}
                    onKeyPress={handleKeyPress}
                    isLoading={isLoadingRequests}
                    width={MobileWindow ? '200px' : '300px'}
                    border={0}
                    bg={COLORS.WHITE_COLOR}
                    _focus={{ borderColor: 'transparent' }}
                    fontSize={COMMONFONTSIZE.MEDIUM_FONT_SIZE}
                    paddingRight={searchQuery ? '40px' : '12px'}
                  />
                  {searchQuery && (
                    <Pressable
                      position='absolute'
                      right={2}
                      top={0}
                      bottom={0}
                      justifyContent='center'
                      alignItems='center'
                      onPress={handleClearSearch}
                      p={1}
                      borderRadius={4}
                      _pressed={{ bg: COLORS.BORDER_COLOR }}
                    >
                      <Svg width='16' height='16' viewBox='0 0 24 24' fill='none'>
                        {clearIconSVG}
                      </Svg>
                    </Pressable>
                  )}
                </Box>
              </HStack>
            </HStack>
          )}

          {/* Requests Table */}
          {requests?.length > 0 ? (
            <Box bg={COLORS.WHITE_COLOR} borderRadius={8} borderWidth={1} borderColor={COLORS.BORDER_COLOR} overflow="hidden">
              <Box overflowX="auto">
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ backgroundColor: COLORS.BADGE_GRAY_COLOR }}>
                      <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold' }}>Vendor Name</th>
                      <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold' }}>Event Name</th>
                      <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold' }}>Vendor Email</th>
                      <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold' }}>Status</th>
                      <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold' }}>Requested At</th>
                      <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold' }}>Reason</th>
                      <th style={{ padding: '12px', textAlign: 'center', fontWeight: 'bold' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {requests.map((request, index) => (
                      <tr key={request?._id || index} style={{ borderBottom: `1px solid ${COLORS.BORDER_COLOR}` }}>
                        <td style={{ padding: '12px' }}>{request?.vendor_name || 'N/A'}</td>
                        <td style={{ padding: '12px' }}>{request?.event_name || 'N/A'}</td>
                        <td style={{ padding: '12px' }}>{request?.vendor_email || 'N/A'}</td>
                        <td style={{ padding: '12px' }}>
                          <Text fontWeight={'bold'} color={getStatusColor(request?.status)}>
                            {request?.status ? request.status.charAt(0).toUpperCase() + request.status.slice(1) : 'N/A'}
                          </Text>
                        </td>
                        <td style={{ padding: '12px' }}>{formatDate(request?.created_at)}</td>
                        <td style={{ padding: '12px' }}>{request?.reason || 'N/A'}</td>
                        <td style={{ padding: '12px', textAlign: 'center' }}>
                          <HStack space={2} justifyContent="center">
                            <CustomButton
                              text="Reject"
                              onPress={() => handleRejectClick(request)}
                              isOutline
                              bgColor={COLORS.REJECT_TEXT_COLOR}
                              textColor={COLORS.REJECT_TEXT_COLOR}
                              fontSize={COMMONFONTSIZE.MEDIUM_FONT_SIZE}
                              width="auto"
                              height="auto"
                              pl={4}
                              pr={4}
                            />
                            <CustomButton
                              text="Re-consider"
                              onPress={() => handleApproveClick(request)}
                              isOutline
                              bgColor={COLORS.SUCCESS_TOAST_COLOR}
                              textColor={COLORS.SUCCESS_TOAST_COLOR}
                              fontSize={COMMONFONTSIZE.MEDIUM_FONT_SIZE}
                              width="auto"
                              height="auto"
                              pl={4}
                              pr={4}
                            />
                          </HStack>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Box>

              {isLoadingRequests && (
                <Box p={8} textAlign="center">
                  <Spinner color={COLORS.THEME_COLOR} size="lg" />
                </Box>
              )}
            </Box>
          ) : requests?.length === 0 && !isLoadingRequests ? (
            <NoDataFound dataName="quotation requests" />
          ) : null}

          {/* Pagination Info */}
          {paginationInfo.total > 0 && requests.length > 0 && (
            <Box mt={4} textAlign="center">
              <Text fontSize={COMMONFONTSIZE.SMALL_FONT_SIZE} color={COLORS.PARTNERS_HEADING_COLOR}>
                Page {paginationInfo.page} • Total {paginationInfo.total} request{paginationInfo.total !== 1 ? 's' : ''}
              </Text>
            </Box>
          )}

          {/* Load More Button */}
          {hasMore && requests.length > 0 && !isLoadingRequests && (
            <Box mt={4} display="flex" justifyContent="center" alignSelf={'center'}>
              <CustomButton
                text='Load More'
                onPress={handleLoadMore}
                isLoading={isLoadingRequests}
              />
            </Box>
          )}
        </Box>
      )}

      {/* Confirmation Modal */}
      {!MobileWindow ? (
        <CustomModal
          isOpen={showConfirmModal}
          onClose={closeConfirmModal}
          header={`${actionType === 'approve' ? 'Re-consider' : 'Reject'} Quotation Request`}
          body={getConfirmModalBody()}
          footer={getConfirmModalFooter()}
          width="max-content"
        />
      ) : (
        <MobileModelSheet
          isOpen={showConfirmModal}
          onClose={closeConfirmModal}
          header={`${actionType === 'approve' ? 'Re-consider' : 'Reject'} Quotation Request`}
          body={getConfirmModalBody()}
          footer={getConfirmModalFooter()}
          maxH="80vh"
        />
      )}
    </>
  );
};

export default QuotationRequests;
